@import '../../utils/control-density.css';
@import '../../utils/control-density-inset.css';
@import '../../utils/control-width.css';
@import '../../utils/focus-ring.css';
@import '../../utils/interaction-fill.css';
@import '../../utils/control-inactive.css';

:host {
  display: block;
  --_tab-group-surface-border: var(--color-border-tertiary);
  --_tab-group-divider: var(--color-border-secondary);
}

:host(.tab-group-host--on-faint) {
  --_tab-group-selected-fill: var(--color-interaction-active);
}

:host(.tab-group-host--on-medium) {
  --_tab-group-surface-border: var(--color-border-on-medium-background-tertiary);
  --_tab-group-divider: var(--color-border-on-medium-background-secondary);
  --_tab-group-selected-fill: var(--color-interaction-on-medium-background-active);
}

:host(.tab-group-host--on-bold) {
  --_tab-group-surface-border: var(--color-border-on-bold-background-tertiary);
  --_tab-group-divider: var(--color-border-on-bold-background-secondary);
  --_tab-group-selected-fill: var(--color-interaction-on-bold-background-active);
}

:host(.tab-group-host--on-strong) {
  --_tab-group-surface-border: var(--color-border-on-strong-background-tertiary);
  --_tab-group-divider: var(--color-border-on-strong-background-secondary);
  --_tab-group-selected-fill: var(--color-interaction-on-strong-background-active);
}

:host(.tab-group-host--on-translucent) {
  --_tab-group-surface-border: var(--color-translucent-border-tertiary);
  --_tab-group-divider: var(--color-translucent-border-secondary);
  --_tab-group-selected-fill: var(--color-translucent-interaction-active);
}

:host(.tab-group-host--on-inverted) {
  --_tab-group-surface-border: var(--color-inverted-border-tertiary);
  --_tab-group-divider: var(--color-inverted-border-secondary);
  --_tab-group-selected-fill: var(--color-inverted-interaction-active);
}

:host(.tab-group-host--on-media) {
  --_tab-group-surface-border: var(--color-media-border-tertiary);
  --_tab-group-divider: var(--color-media-border-secondary);
  --_tab-group-selected-fill: var(--color-media-interaction-active);
}

:host(.tab-group-host--on-always-dark) {
  --_tab-group-surface-border: var(--color-always-dark-border-tertiary);
  --_tab-group-divider: var(--color-always-dark-border-secondary);
  --_tab-group-selected-fill: var(--color-always-dark-interaction-active);
}

.tab-list {
  display: flex;
  align-items: center;
  gap: var(--ds-control-gap);
  position: relative;
  width: max-content;
  max-width: 100%;
  height: var(--ds-control-height);
  padding: calc(var(--dimension-space-025) - var(--dimension-stroke-width-012));
  background-color: var(--color-background-secondary);
  border: var(--dimension-stroke-width-012) solid var(--_tab-group-surface-border);
  border-radius: var(--dimension-radius-half);
  box-sizing: border-box;
}

:host(.ds-control-width--fill) .tab-list {
  width: 100%;
}

:host(.tab-group-host--surface) .tab-list {
  background-color: transparent;
}

.tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--ds-control-gap);
  padding: 0 var(--ds-control-padding-inline);
  background-color: transparent;
  border: none;
  border-radius: var(--dimension-radius-half);
  cursor: default;
  -webkit-user-select: none;
  user-select: none;
  color: var(--color-foreground-secondary);
  transition: color var(--effect-motion-short-2);
  flex-shrink: 0;
  height: var(--ds-control-height);
  min-width: var(--ds-control-height);
  box-sizing: border-box;
  --_dot: var(--color-foreground-bold-brand);
  /* Hover/press: `.ds-interaction-fill` fills cover the complete segment. */
}

:host(.ds-control-width--fill) .tab {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
}

:host(.ds-control-width--fill) .tab__content {
  max-width: 100%;
  overflow: hidden;
}

:host(.ds-control-width--fill) .tab__label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.tab--selected {
  color: var(--color-foreground-primary);
  background-color: var(--color-background-primary);
  /* Inset stroke on interaction-fill ::after — stays above hover wash. */
  --ds-interaction-border-width: var(--dimension-stroke-width-012);
  --ds-interaction-border-color: var(--_tab-group-surface-border);
}

:host(.tab-group-host--surface) .tab--selected {
  background-color: var(--_tab-group-selected-fill);
}

.tab--selected:not(.ds-interaction-fill) {
  box-shadow: inset 0 0 0 var(--ds-interaction-border-width) var(--ds-interaction-border-color);
}

/* Inactive: `.ds-control-inactive` (50% opacity) — applied via class in TSX. */

.tab__content {
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--ds-control-gap);
  min-width: 0;
}

.tab__content--dot {
  padding-right: var(--dimension-space-050);
}

.tab__label {
  padding: 0 var(--ds-control-label-inset);
  white-space: nowrap;
}

.tab__icon {
  flex-shrink: 0;
}

.tab__dot {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  pointer-events: none;
}

.on-medium {
  color: var(--color-foreground-on-medium-background-secondary);
}

.on-medium.tab--selected { color: var(--color-foreground-on-medium-background-primary); }

.on-bold {
  color: var(--color-foreground-on-bold-background-secondary);
}

.on-bold.tab--selected { color: var(--color-foreground-on-bold-background-primary); }

.on-strong {
  color: var(--color-foreground-on-strong-background-secondary);
}

.on-strong.tab--selected { color: var(--color-foreground-on-strong-background-primary); }

.on-translucent { color: var(--color-translucent-foreground-secondary); }

.on-translucent.tab--selected { color: var(--color-translucent-foreground-primary); }

.on-inverted { color: var(--color-inverted-foreground-secondary); }

.on-inverted.tab--selected { color: var(--color-inverted-foreground-primary); }

.on-media { color: var(--color-media-foreground-secondary); }

.on-media.tab--selected { color: var(--color-media-foreground-primary); }

.on-always-dark {
  color: var(--color-always-dark-foreground-secondary);
}

.on-always-dark.tab--selected { color: var(--color-always-dark-foreground-primary); }

.tab-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: var(--dimension-stroke-width-012);
  align-self: center;
  padding: 0;
  flex-shrink: 0;
  pointer-events: none;
}

.tab-divider__line {
  width: var(--dimension-stroke-width-012);
  height: var(--typography-lineheight-sm);
  flex-shrink: 0;
  background-color: var(--_tab-group-divider);
}
